import { Component, Element, Host, Prop, h } from '@stencil/core';

import bannerActions from '../../../actions/actions.interactive-banner';
import bannerValidateActions from '../../../actions/actions.validation.interactive-banner';
import cameraScanIcon from '../../../assets/logos/camera_scan.svg';
import { state as bannerState } from '../../../store/store.interactive-banner';
import { shiftDigitalTradeInStart, trackBannerLinkClick } from '../../../tracking/tracking.interactiveBanner';
import { ACS_EVENT_PROPS } from '../../../utils/utils.asc-analytics';
import { BrowserUtils } from '../../../utils/utils.browser';
import { bannerSizeType } from '../../../utils/utils.interactive-banner';
import { MultiScannerResult } from '../../../utils/utils.multiscanner';

@Component({
  tag: 'banner-vin',
  styleUrl: 'banner-vin.css',
  shadow: true,
})
export class BannerVin {
  @Element() el: HTMLElement;

  @Prop({ mutable: true }) showVinScan: boolean = false;
  @Prop({ mutable: true }) vin: string = '';
  @Prop({ mutable: true }) errorMessage: string;
  @Prop({ mutable: true }) hasError: boolean = false;
  @Prop() showDesktop: boolean = false;
  @Prop({ mutable: true }) mileage: number;
  @Prop() scannerEnabled: boolean = BrowserUtils.IS_MOBILE;
  @Prop() bannerVinSize: bannerSizeType = 'large';

  handleSubmit() {
    shiftDigitalTradeInStart();
    this.validate();
    if (this.hasError) {
      bannerActions.onTrackStartMyOffer(false);
      return;
    }
    this.vin = bannerState.vin;
    bannerActions.onFindByVin(this.vin);
  }

  onInputChange(event) {
    const value = event.detail.trim().toUpperCase();

    this.vin = value;
    this.updateStatus();
    this.validate();
  }

  onInputBlur() {
    this.updateStatus();
    this.validate();
  }

  updateStatus() {
    bannerActions.updateVinInputStatus(this.vin);
  }

  validate() {
    this.hasError = !bannerValidateActions.onValidateBannerVin(this.vin);
  }

  showVinDialog(event: MouseEvent) {
    event.preventDefault();
    const modal = this.el.shadowRoot.querySelector('ico-modal#wheres-my-vin') as HTMLIcoModalElement;
    modal.open();
  }

  handleCloseWebscanner() {
    this.showVinScan = false;
  }

  handleClickScanner() {
    const webScanner = this.el.shadowRoot.querySelector('webscanner-component');
    if (webScanner !== null) {
      webScanner.start();
      this.showVinScan = true;
    }
  }

  handleScannerResult(result: MultiScannerResult) {
    const { vin, error } = result;
    if (!error) {
      bannerState.vin = vin;
      bannerActions.onFindByVin(vin);
    }
  }

  render() {
    const bannerVinInputGroupClass = {
      'vin-input-group': true,
      'flex-col': this.bannerVinSize === 'small' || this.bannerVinSize === 'xsmall',
      'flex-row': this.showDesktop || this.bannerVinSize === 'medium',
    };
    const errorMessageMobileClass = {
      'flex w-full': true,
      hidden: this.showDesktop,
    };
    const errorMessageDesktopClass = {
      'flex w-full': true,
      hidden: !this.showDesktop,
    };
    const findMyVehicleClass = {
      'w-full': this.bannerVinSize === 'small' || this.bannerVinSize === 'xsmall',
      'basis-1/4 min-w-183': this.bannerVinSize === 'medium',
      'w-52': this.showDesktop,
    };
    const whereIsMyVinClass = {
      flex: true,
      hidden: this.hasError,
    };
    const sectionVinClass = {
      flex: this.scannerEnabled,
    };
    const textBoxVinClass = {
      'mb-1': true,
      'inline-block w-full': this.scannerEnabled,
    };

    const bannerVinInputClass = {
      'basis-2/3': this.bannerVinSize === 'large' || this.bannerVinSize === 'small' || this.bannerVinSize === 'xsmall',
      'basis-3/4': this.bannerVinSize === 'medium',
    };

    const scannerClass = { hidden: !this.showVinScan };

    return (
      <Host>
        <div class="flex flex-col">
          <div class={bannerVinInputGroupClass}>
            <div class={bannerVinInputClass}>
              <ico-modal id="wheres-my-vin" slot="modal">
                <div class="max-w-sm flex flex-wrap justify-center">
                  <div class="basis-full">
                    <h3 class="text-md text-left text-clrs-dark-grey">Where's my VIN?</h3>
                    <h4 class="text-sm text-left font-normal text-clrs-dark-grey pb-2">The Vehicle Identification Number (VIN) always has exactly 17 characters</h4>
                  </div>
                  <div class="basis-full">
                    <svg-icon icon="wheres-my-vin" />
                  </div>
                </div>
              </ico-modal>
              <div class="flex flex-col gap-x-5">
                <div class="flex basis-2/3 flex-col">
                  <div class={sectionVinClass}>
                    <div class={textBoxVinClass}>
                      <input-text-box
                        id="vin"
                        label="Enter the 17-digit VIN"
                        hasError={this.hasError}
                        onValueChange={(e) => this.onInputChange(e)}
                        onBlur={this.onInputBlur.bind(this)}
                        onFocusin={() => shiftDigitalTradeInStart()}
                        value={this.vin}
                        hasGrayPlaceHolder
                        maxLength={17}
                      />
                    </div>
                    {this.scannerEnabled && (
                      <div class="z-[1] h-8 w-8 -ml-10 py-2 rounded-r" onClick={() => this.handleClickScanner()}>
                        <img alt="scan" src={cameraScanIcon}></img>
                      </div>
                    )}
                  </div>
                  <banner-error class={errorMessageMobileClass} hasError={this.hasError} errorMessage={this.errorMessage} hiddenMode={true}></banner-error>
                  <div class={whereIsMyVinClass}>
                    <ico-hyperlink
                      content="primary"
                      class="text-xs mt-[1px]"
                      href="#"
                      onClick={(e) => {
                        shiftDigitalTradeInStart();
                        trackBannerLinkClick(ACS_EVENT_PROPS.WHERE_IS_MY_VIN);
                        this.showVinDialog(e);
                      }}>
                      Where is my VIN?
                    </ico-hyperlink>
                  </div>
                </div>
              </div>
            </div>
            <div class={findMyVehicleClass}>
              <ico-button label="Start My Offer" narrow buttonSize="large" whenClicked={this.handleSubmit.bind(this)} />
            </div>
          </div>
          <banner-error class={errorMessageDesktopClass} hasError={this.hasError} errorMessage={this.errorMessage} hiddenMode={true}></banner-error>

          {this.scannerEnabled && (
            <webscanner-component
              onScanSuccess={(e) => this.handleScannerResult(e.detail)}
              onClose={() => this.handleCloseWebscanner()}
              class={scannerClass}
              trackEventElement="ico-kbb"
              bottomContent={
                <span>
                  Scan your VIN barcode located inside the driver's doorjamb.
                  <br />
                  Rotate your phone if needed.
                </span>
              }
            />
          )}
        </div>
      </Host>
    );
  }
}
